import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  Input, OnDestroy
} from '@angular/core';
import { ISubjectSelectionSection } from '@ispm/therapies/interfaces/view-configuration.interface';
import { get, isEmpty } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { ConfigurationStoreHandler } from '../../../configuration/services/configuration-store-handler.service';
import { filter, take } from "rxjs/operators";
import { ISubjectIdentifier } from "@ispm/therapies/interfaces/subject.interface";
import {
  RESOURCE_TYPE_NAME
} from "@ispm/therapies/constants/default-clinical-recommendation-sorting-configuration.constant";
import { TherapiesActionCreatorService } from "@ispm/therapies/services/therapies-action-creator.service";

@Component({
  selector: 'ispm-treatment-subject-wrapper',
  templateUrl: './treatment-subject-wrapper.component.html',
  styleUrls: ['./treatment-subject-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class TreatmentSubjectWrapperComponent implements OnInit, OnDestroy {

  @Output()
  public onchangeOpinionOfMI: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  public onSubjectChange: EventEmitter<ISubjectIdentifier> = new EventEmitter<ISubjectIdentifier>();

  @Output()
  public onDataReceived: EventEmitter<any> = new EventEmitter<any>();

  @Input()
  public reflexMessage: string;

  @Input()
  public isEditable: boolean;

  @Input()
  public showInReportToggleActive: boolean;

  @Input()
  public showInMIToggleActive: boolean;

  @Input()
  public reportEditable: boolean;

  @Input()
  public subjectsListConfiguration: ISubjectSelectionSection;

  public resourcesMap$: Observable<any>;

  public formId: string;
  public additionalSearchValues$: Observable<any>;

  private destroyer$: Subject<any> = new Subject<any>();
  isLoadingSubject = false;

  constructor(
    private configurationAC: ConfigurationStoreHandler,
    private therapiesAC: TherapiesActionCreatorService,
  ) { }

  private setFormId() {
    if (this.subjectsListConfiguration.type === 'form') {
      this.configurationAC.getCurrentStepPurpose()
        .pipe(
          filter(step => !isEmpty(step)),
          take(1),
        )
        .subscribe(step => this.formId = this.getFormId(step));
    }
  }

  ngOnInit(): void {
    this.additionalSearchValues$ = this.configurationAC.getCurrentConfig();
    this.resourcesMap$ = this.therapiesAC.prefetchTestResources();

    this.setFormId();
  }

  public processSubject($event: any): void {

    const RequestMIStatus = get($event.form, '_tabActiveRecordMeta.subject.IsMI', null);
    if (RequestMIStatus === true || RequestMIStatus === false) {
      this.onchangeOpinionOfMI.emit(RequestMIStatus);
      return;
    }

    const formChangePaths = (this.subjectsListConfiguration.onFormChangePaths || []);
    const controlMapper = formChangePaths.find(path => this.simpleFindControlMapper($event.form, path)) ||
      formChangePaths.find(path => this.findControlMapper($event.form, path));

    if (!isEmpty(controlMapper)) {
      if (controlMapper.passWholeData) {
        if (controlMapper.passWholeData === 'map') {
          this.onDataReceived.emit(this.mapData(controlMapper.mapData, get($event.form, controlMapper.mapData?.path)));
        }
        else {
          this.onDataReceived.emit(get($event.form, controlMapper.control));
        }
      }
      else {
        this.onDataReceived.emit([]);
      }

      const selectedSubjectId = controlMapper.subjectIdentifier ?
        get($event.form, controlMapper.subjectIdentifier?.subjectId) :
        get($event.form, controlMapper.mapData?.path || controlMapper.control)?.id;

      const subjectIdentifier: ISubjectIdentifier = {
        subjectId: selectedSubjectId,
        subjectSourceType: RESOURCE_TYPE_NAME.THERAPIES,
      };

      const sst = controlMapper.subjectIdentifier ? get($event.form, controlMapper.subjectIdentifier?.subjectSourceType, null) : controlMapper?.subjectSourceType;
      if (sst) {
        subjectIdentifier.subjectSourceType = sst;
      }

      const sIP = get($event.form, controlMapper.subjectIdentifier?.subjectIdPath || 'id', null);
      if (sIP) {
        subjectIdentifier.subjectIdPath = sIP;
      }

      this.onSubjectChange.emit(subjectIdentifier);
    }
    else {
      if (isEmpty(formChangePaths)) {
        this.onDataReceived.emit([]);
        this.onSubjectChange.emit($event);
      }
    }
  }

  ngOnDestroy() {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  private mapData(mapConf, data: any) {
    let mappedData = { };
    if (!isEmpty(mapConf.fields)) {
      mapConf.fields.forEach(field => {
        mappedData[field.name] = get(data, field.path);
      });
    } else {
      mappedData = data;
    }
    return mapConf.type === 'array' ? [mappedData] : mappedData;
  }

  private simpleFindControlMapper(eventForm, pathConf) {
    const eventControl = get(eventForm, pathConf.control, null);

    return !!pathConf.value && !!eventControl && get(pathConf, 'useSimpleSearch', false)
      && typeof pathConf.value === 'string' && typeof eventControl === 'string'
      && pathConf.value === eventControl
      && !pathConf.hasOwnProperty('ignoreEmptyPath');
  }

  private findControlMapper(eventForm, pathConf) {
    const eventControl = get(eventForm, pathConf.control, null);

    if (!eventControl) {
      return false;
    }

    if (pathConf.value && pathConf.value !== eventControl) {
      return false;
    }

    if (!pathConf.hasOwnProperty('ignoreEmptyPath')) {
      return true;
    }

    if (isEmpty(get(eventForm, pathConf.ignoreEmptyPath))) {
      return false;
    }

    return true;
  }

  private getFormId(step: string) {
    const formConf = get(this.subjectsListConfiguration, 'formConf.formsPerStep');

    if (formConf) {
      const config = formConf.find(conf => conf.steps.includes(step.toLowerCase()));

      return config.form;
    }

    return get(this.subjectsListConfiguration, 'formConf.formId');
  }
}
